// src/App.js

import React, { useEffect, useState, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';

// Componentes leves - importação estática
import Home            from './Home';
import NameForm        from './NameForm';
import EmailForm       from './EmailForm';
import EmailConfirmForm from './EmailConfirmForm';
import CpfForm         from './CpfForm';
import InstantCouponOffer from './InstantCouponOffer';
import PhoneForm       from './PhoneForm';
import DocumentIdForm  from './DocumentIdForm';
import TermsOfUse      from './TermsOfUse';
import SignupHistoryGuard from './components/SignupHistoryGuard';
import TermsOfUsePublic from './TermsOfUsePublic';
import PrivacyPolicy   from './PrivacyPolicy';
import DadosExclusao   from './DadosExclusao';
import SegundaVia      from './SegundaVia';
// Bloco 3 (CFM 2.454/2026, Anexo III-I + Art. 11): página pública
// de transparência sobre o uso de IA — acessível sem login.
import TransparenciaIA from './TransparenciaIA';
// Promoção de 50% OFF para pacientes que deixarem avaliação positiva no
// Reclame Aqui — página pública referenciada pelo texto de pagamento
// automático no ConsultaChat.
import ReclameAquiPage from './ReclameAquiPage';
// Promoção App Store / Google Play (AVALIEAPP) — encerrada 31/07/2026.
// Página retirada do ar em 15/08/2026 (301 + Navigate para a home).
import AvalieAppPage from './AvalieAppPage';
import ReviewsPage from './ReviewsPage';
import DiretorPage from './DiretorPage';
// Hubs de descoberta GEO (agosto/2026) — páginas-entidade otimizadas para
// citação por LLMs (Gemini, ChatGPT, Perplexity) em queries de descoberta
// sobre teleconsulta por chat e desambiguação da marca. Ver
// docs/geo/plano-geo-gemini-chatgpt-2026-08.md.
import HubConsultaMedicaPorChatPage from './HubConsultaMedicaPorChatPage';
import HubPlataformasTeleconsultaPage from './HubPlataformasTeleconsultaPage';
import HubConsultaMaisConsultaPlusPage from './HubConsultaMaisConsultaPlusPage';
import HubCobrancaCartaoPage from './HubCobrancaCartaoPage';
import EmbedSofia from './EmbedSofia';
import AdminHealthDashboard from './AdminHealthDashboard';
// Onda 1B hardening 2026-06: AdminGuard protege rotas administrativas
// com Firebase Auth + custom claim `role`. Coexiste com a senha legada
// (cada painel ainda tem sua propria validacao) ate todos os usuarios
// terem migrado. O guard so e aplicado quando habilitado por
// REACT_APP_ADMIN_REQUIRE_LOGIN=true.
import AdminGuard from './admin/AdminGuard';
import Autenticar      from './Autenticar';
import PreviaDocumento from './PreviaDocumento';
import DataAtestado    from './DataAtestado';
import { buildIndiqueWaUrl } from './indiqueWhatsAppText';

// Telemetria e utilidades
import { initTelemetry } from './telemetry';
import { logUxEvent, setUxUserFromStorage } from './telemetry/uxLogger';
import { useScreenView } from './telemetry/useScreenView';
import { useVersionCheck } from './hooks/useVersionCheck';
import { UpdateBanner } from './components/UpdateBanner';
import { ForceUpdateModal } from './components/ForceUpdateModal';
import SupportChatWidget from './components/SupportChatWidget';
import PatientSupportFooter from './components/PatientSupportFooter';
import AnimatedOutlet from './components/AnimatedOutlet';
import ErrorScreen, { pickErrorVariant } from './components/ErrorScreen';
import ScrollToTop from './components/ScrollToTop';
import { isPaymentConfirmedFor, migrateLegacyFlag } from './utils/paymentFlag';
import { isActiveChannelOwnedBy } from './utils/consultationSessionBound';
import { shouldRefreshForNewVersion } from './utils/appVersion';
import EmailPreviewGate from './components/EmailPreviewGate';
import { isOrgHost, isOrgAllowedPath, captureLandingParams, buildAppUrl } from './lib/siteHosts';

// Onda 1B hardening 2026-06: gate de paineis admin via AdminGuard.
// Quando REACT_APP_ADMIN_REQUIRE_LOGIN=true, todas as rotas admin
// exigem login Firebase + custom claim `role`. Default false mantem
// fluxo legado (senha+token) durante a transicao.
const ADMIN_REQUIRE_LOGIN =
  (process.env.REACT_APP_ADMIN_REQUIRE_LOGIN || 'false').toLowerCase() === 'true';
const withAdminGuard = (allowedRoles, element, title) =>
  ADMIN_REQUIRE_LOGIN ? <AdminGuard allowedRoles={allowedRoles} title={title}>{element}</AdminGuard> : element;

// Lazy com retry automático: tenta até 3x antes de desistir
function lazyWithRetry(importFn, retries = 3, delay = 1500) {
  return React.lazy(() => {
    const attempt = (remaining) =>
      importFn().catch((err) => {
        if (remaining <= 1) throw err;
        return new Promise((r) => setTimeout(r, delay)).then(() => attempt(remaining - 1));
      });
    return attempt(retries);
  });
}

// Componentes pesados - lazy loading com retry automático.
// webpackPrefetch nos chunks da jornada do paciente (Pagamento -> AnamneseChat
// -> ConsultaChat): webpack injeta <link rel="prefetch"> no <head> e o
// navegador baixa esses chunks em paralelo quando idle, antes do usuario
// chegar na rota. Resolve LCP P75 9s em /consulta/chat em iOS (auditoria
// UX 28/06): o contact-section com min-height 20rem virou o LCP target e
// so renderizava apos o chunk lazy chegar (~9s em iOS lenta). Com prefetch
// o chunk costuma chegar antes da navegacao, reduzindo LCP a ~2s.
const PaymentScreen   = lazyWithRetry(() => import(/* webpackPrefetch: true */ './Pagamento'));
const AnamneseChat    = lazyWithRetry(() => import(/* webpackPrefetch: true */ './AnamneseChat'));
// webpackChunkName "consulta-chat": nome estavel para o chunk permitir que o
// inject-version.js injete <link rel="preload"> condicional no index.html —
// entrada DIRETA em /consulta/chat (link de broadcast/notificacao) nao se
// beneficia do idle prefetch e pagava LCP ~8s em iOS (auditoria 15/07).
const ConsultaChat    = lazyWithRetry(() => import(/* webpackChunkName: "consulta-chat", webpackPrefetch: true */ './ConsultaChat'));
const DoctorDashboard = lazyWithRetry(() => import('./DoctorDashboard'));
const MedicoPainel = lazyWithRetry(() => import('./MedicoPainel'));
const NotaFiscal      = lazyWithRetry(() => import('./NotaFiscal'));
const NotaFiscalAntiga = lazyWithRetry(() => import('./NotaFiscalAntiga'));
const PromoScreen     = lazyWithRetry(() => import('./PromoScreen'));
const Promo           = lazyWithRetry(() => import('./Promo'));
const PrescriptionReminderPage = lazyWithRetry(() => import('./PrescriptionReminderPage'));
const CareReminderPage = lazyWithRetry(() => import('./CareReminderPage'));

const SupportDashboard = lazyWithRetry(() => import('./SupportDashboard'));
const ControleDashboard = lazyWithRetry(() => import('./ControleDashboard'));
const CouponsDashboard = lazyWithRetry(() => import('./CouponsDashboard'));
const UXMonitorDashboard = lazyWithRetry(() => import('./UXMonitorDashboard'));
const AmigoIntro = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoIntro })));
const AmigoStepNome = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepNome })));
const AmigoStepCpf = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepCpf })));
const AmigoStepEmail = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepEmail })));
const AmigoStepEmailConfirm = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepEmailConfirm })));
const AmigoStepTelefone = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepTelefone })));
const AmigoStepAceite = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepAceite })));
const AmigoStepPronto = lazyWithRetry(() => import('./AmigoWizard').then((m) => ({ default: m.AmigoStepPronto })));
const AmigoProgramTerms = lazyWithRetry(() => import('./AmigoProgramTerms'));
const CopaAmigosDoPeitoTerms = lazyWithRetry(() => import('./CopaAmigosDoPeitoTerms'));
const DivulguePage = lazyWithRetry(() => import('./DivulguePage'));
const MelhoresAmigosProgramTerms = lazyWithRetry(() => import('./MelhoresAmigosProgramTerms'));
const FirstConsultPromoTerms = lazyWithRetry(() => import('./FirstConsultPromoTerms'));
const VideoConcursoTerms = lazyWithRetry(() => import('./VideoConcursoTerms'));
const MeuProgramaAmigo = lazyWithRetry(() => import('./MeuProgramaAmigo'));
const NoticiasPortal = lazyWithRetry(() => import('./NoticiasPortal'));
const NoticiasCategoria = lazyWithRetry(() => import('./NoticiasCategoria'));
const ConteudoArticle = lazyWithRetry(() => import('./ConteudoArticle'));
const SocialPage = lazyWithRetry(() => import('./SocialPage'));
const SocialMarlieteCupomPage = lazyWithRetry(() => import('./SocialMarlieteCupomPage'));
const MarlieteCouponTerms = lazyWithRetry(() => import('./MarlieteCouponTerms'));
const Fastcheck = lazyWithRetry(() => import('./Fastcheck'));
const FastcheckTerms = lazyWithRetry(() => import('./FastcheckTerms'));

const BACKEND_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Revalidação de estorno com a sessão aberta (ver efeito no PatientLayout).
// 8s é o teto de atraso para a barra "Pagar" reaparecer; o gap mínimo dedupa a
// rajada de visibilitychange + focus + pointerdown que chega junta no mobile.
const PAYMENT_RECHECK_MS = 8000;
const PAYMENT_RECHECK_MIN_GAP_MS = 3000;

const prefetchMap = {
  '/telefone': [() => import('./TermsOfUse')],
  '/termos': [() => import('./AnamneseChat')],
  '/triagem': [() => import('./ConsultaChat')],
};

function usePrefetchNextRoute() {
  const location = useLocation();
  useEffect(() => {
    const loaders = prefetchMap[location.pathname];
    if (!loaders) return;
    const timer = setTimeout(() => {
      loaders.forEach((loader) => loader().catch(() => {}));
    }, 400);
    return () => clearTimeout(timer);
  }, [location.pathname]);
}

const clearChatCache = () => {
  try {
    const criticalKeys = new Set([
      'patientId',
      'fullName',
      'paymentConfirmed',
      'activeChannelId',
      'appSession',
      'finalizedAt',
      'chatStartedAt',
      'chatStartedAtFor',
      'chatLastSeenAt',
      'anamneseProntuario',
      'anamneseProntuarioFor',
      'prontuarioSent',
      'paymentSent',
      'conversionTracked',
      'loginRole',
      'topic',
      'app_version',
      'app_build_time',
      'dashboardAuthorized',
    ]);

    Object.keys(localStorage).forEach((k) => {
      const isChatKey = k.startsWith('str_') || k.includes('stream') || k.includes('chat');
      if (!criticalKeys.has(k) && isChatKey) {
        localStorage.removeItem(k);
      }
    });
  } catch (err) {
    console.warn('Erro ao limpar cache do chat:', err);
  }
};

const LOADING_TIMEOUT_MS = 10000;
const RETRY_COUNT_KEY = 'recovery_retry_count';
const RETRY_SHOW_HOME_AFTER = 3;

const getRetryCount = () => {
  try { return parseInt(sessionStorage.getItem(RETRY_COUNT_KEY) || '0', 10); } catch { return 0; }
};
const incrementRetryCount = () => {
  try { const n = getRetryCount() + 1; sessionStorage.setItem(RETRY_COUNT_KEY, String(n)); return n; } catch { return 1; }
};

/** Primeira tentativa só recarrega; a partir da segunda limpa também o cache do chat. */
const retryWithEscalation = () => {
  const n = incrementRetryCount();
  if (n >= 2) clearChatCache();
  window.location.reload();
};

/** Versão antiga em cache: apaga caches e service worker e recarrega sem cache. */
const reloadFreshVersion = () => {
  incrementRetryCount();
  try {
    if ('caches' in window) caches.keys().then((names) => names.forEach((n) => caches.delete(n))).catch(() => {});
    if ('serviceWorker' in navigator) navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
  } catch {}
  setTimeout(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('cb', String(Date.now()));
      window.location.replace(url.toString());
    } catch { window.location.reload(); }
  }, 150);
};

const goHome = () => {
  try { sessionStorage.removeItem(RETRY_COUNT_KEY); } catch {}
  window.location.href = '/';
};

const LoadingFallback = () => {
  const [timedOut, setTimedOut] = useState(false);
  const [showHome, setShowHome] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedOut(true);
      setShowHome(getRetryCount() >= RETRY_SHOW_HOME_AFTER);
    }, LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (timedOut) {
    return (
      <ErrorScreen
        variant={pickErrorVariant(null, 'timeout')}
        onPrimary={retryWithEscalation}
        onHome={showHome ? goHome : undefined}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F9FAFB',
        fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        {/* Spinner estilo Material (SVG): arco com pontas arredondadas que
            cresce/encolhe enquanto gira — o antigo (borda girando) deformava
            em telas com renderização subpixel e parecia uma elipse. */}
        <svg
          viewBox="0 0 50 50"
          role="progressbar"
          aria-label="Carregando"
          style={{
            width: '36px',
            height: '36px',
            margin: '0 auto 12px',
            display: 'block',
            animation: 'cms-spinner-rotate 1.6s linear infinite',
          }}
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="#00A4E0"
            strokeWidth="4"
            strokeLinecap="round"
            style={{ animation: 'cms-spinner-dash 1.4s ease-in-out infinite' }}
          />
        </svg>
        <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>Carregando...</p>
        <style>{`
          @keyframes cms-spinner-rotate { 100% { transform: rotate(360deg); } }
          @keyframes cms-spinner-dash {
            0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
            50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
            100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
          }
        `}</style>
      </div>
    </div>
  );
};

const readStoredAttribution = () => {
  try {
    const raw = localStorage.getItem('attribution');
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || (!obj.gclid && !obj.gbraid && !obj.wbraid && !obj.fbclid && !obj.twclid && !obj.msclkid && !obj.rdtCid && !obj.ttclid && !obj.utmSource)) return null;
    const base = {
      gclid: obj.gclid || null,
      gbraid: obj.gbraid || null,
      wbraid: obj.wbraid || null,
      fbclid: obj.fbclid || null,
      twclid: obj.twclid || null,
      msclkid: obj.msclkid || null,
      rdtCid: obj.rdtCid || null,
      ttclid: obj.ttclid || null,
      fbp: obj.fbp || null,
      fbc: obj.fbc || null,
      utmSource: obj.utmSource || null,
      utmMedium: obj.utmMedium || null,
      utmCampaign: obj.utmCampaign || null,
      utmContent: obj.utmContent || null,
      utmTerm: obj.utmTerm || null,
      em: obj.em || null,
      landingPath: obj.landingPath || null,
      capturedAt: obj.capturedAt || null,
      lastTouchAt: obj.lastTouchAt || null,
      lastTouchUtmMedium: obj.lastTouchUtmMedium || null,
      lastTouchUtmCampaign: obj.lastTouchUtmCampaign || null,
    };
    if (typeof window.refreshMetaAttributionCookies === 'function') {
      window.refreshMetaAttributionCookies();
    }
    if (typeof window.getMetaAttributionCookies === 'function') {
      return window.getMetaAttributionCookies(base);
    }
    return base;
  } catch {
    return null;
  }
};

// Restaura `localStorage.attribution` a partir dos backups (cookie legivel e
// IndexedDB) quando o localStorage foi limpo — ex.: Safari ITP expira
// localStorage em 7 dias, mas IndexedDB costuma sobreviver mais tempo.
// O fallback final (cookie httpOnly __session) e aplicado no backend.
const hydrateAttributionFromBackups = () => {
  try {
    if (localStorage.getItem('attribution')) return;
  } catch {
    return;
  }
  // 1) cookie `attribution` (nao-httpOnly, mesmo payload do localStorage)
  try {
    const m = document.cookie.match(/(?:^|;\s*)attribution=([^;]+)/);
    if (m) {
      const parsed = JSON.parse(decodeURIComponent(m[1]));
      if (parsed && (parsed.gclid || parsed.gbraid || parsed.wbraid || parsed.fbclid || parsed.twclid || parsed.msclkid || parsed.rdtCid || parsed.ttclid || parsed.utmSource)) {
        localStorage.setItem('attribution', JSON.stringify(parsed));
        return;
      }
    }
  } catch {}
  // 2) IndexedDB (assincrono; grava de volta no localStorage quando resolver)
  try {
    const req = indexedDB.open('attr_db', 1);
    req.onupgradeneeded = (e) => e.target.result.createObjectStore('attr');
    req.onsuccess = (e) => {
      try {
        const db = e.target.result;
        const get = db.transaction('attr', 'readonly').objectStore('attr').get('v1');
        get.onsuccess = () => {
          try {
            const val = get.result;
            if (val && !localStorage.getItem('attribution')) {
              localStorage.setItem('attribution', JSON.stringify(val));
            }
          } catch {}
        };
      } catch {}
    };
  } catch {}
};

const installPaymentAttributionInterceptor = () => {
  if (typeof window === 'undefined' || window.__attributionInterceptorInstalled) return;
  window.__attributionInterceptorInstalled = true;
  const origFetch = window.fetch;
  if (typeof origFetch !== 'function') return;
  window.fetch = function patchedFetch(input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = String((init && init.method) || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
      const isPaymentCreate = /\/payments\/.+\/(create|checkout|preference|pix\/create)/i.test(url);
      if (isPaymentCreate && method === 'POST') {
        const attr = readStoredAttribution();
        if (attr) {
          const newInit = { ...(init || {}) };
          const headers = new Headers(newInit.headers || {});
          const ct = (headers.get('content-type') || '').toLowerCase();
          let bodyObj = null;
          if (typeof newInit.body === 'string') {
            try { bodyObj = JSON.parse(newInit.body); } catch {}
          } else if (newInit.body && typeof newInit.body === 'object' && !(newInit.body instanceof FormData) && !(newInit.body instanceof Blob)) {
            bodyObj = { ...newInit.body };
          }
          if (bodyObj && typeof bodyObj === 'object' && !bodyObj.attribution) {
            bodyObj.attribution = attr;
            if (!ct) headers.set('content-type', 'application/json');
            newInit.body = JSON.stringify(bodyObj);
            newInit.headers = headers;
            return origFetch.call(this, input, newInit);
          }
        }
      }
    } catch (err) {
      console.warn('[attribution] interceptor falhou:', err?.message || err);
    }
    return origFetch.call(this, input, init);
  };
};

const hardRefreshIfStale = () => {
  try {
    if (/\be2e=1\b/.test(window.location.search)) return;
    // Flag de teste pareado ao `index.html` (linha ~67). Mantém o app fora do
    // modo `isE2e` mas evita o loop de cache-bust em dev/Playwright onde o
    // placeholder `{{APP_VERSION}}` não é substituído.
    if (typeof window !== 'undefined' && window.__SKIP_CACHE_BUST__) return;
    const appVersion = window.APP_VERSION || '';
    const storedVersion = localStorage.getItem('app_version') || '';
    const isPlaceholder = String(appVersion).indexOf('{{') === 0;
    // Só para frente: quando esta página é anterior à versão já conhecida (a
    // cópia de emergência do service worker), recarregar reinstala a versão
    // velha e reinicia a aba à toa — era o que matava a galeria aberta.
    const isStale = shouldRefreshForNewVersion(storedVersion, appVersion);
    // Placeholder = dev server (`npm start`), onde {{APP_VERSION}} nunca é
    // substituído: recarregar é inútil e gera loop infinito de reload, porque o
    // script do index.html roda sessionStorage.clear() a cada carga (apagando o
    // guard 'cache_bust_app' abaixo) sempre que detecta placeholder. Mesma
    // regra "nunca recarregar para placeholder" que o index.html já aplica.
    if (isPlaceholder) return;
    if (!isStale) return;
    if (sessionStorage.getItem('cache_bust_app')) return;
    sessionStorage.setItem('cache_bust_app', '1');

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => reg.unregister());
      });
    }
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.set('v', appVersion || Date.now().toString());
    window.location.replace(url.toString());
  } catch (err) {
    console.warn('Erro ao forçar atualização do cache:', err);
  }
};

const ERROR_BOUNDARY_RECOVER_RE =
  /Loading(?: CSS)? chunk \d+ failed|Failed to fetch dynamically imported module|ChunkLoadError|Unexpected token ['"]?<|can'?t use a channel after client\.disconnect/i;

function tryErrorBoundaryRecover(message) {
  try {
    if (!ERROR_BOUNDARY_RECOVER_RE.test(message)) return false;
    const FLAG = 'eb_chunk_reload_at';
    let last = 0;
    try { last = parseInt(sessionStorage.getItem(FLAG) || '0', 10) || 0; } catch {}
    const now = Date.now();
    if (now - last < 60_000 || typeof window === 'undefined' || !window.location) return false;
    try { sessionStorage.setItem(FLAG, String(now)); } catch {}
    const isDisconnect = /can'?t use a channel after client\.disconnect/i.test(message);
    try {
      logUxEvent(isDisconnect ? 'stream_disconnect_recover' : 'stale_chunk_recover', {
        route: window.location.pathname,
        msg: message.slice(0, 120),
        source: 'error_boundary',
      });
    } catch {}
    if ('caches' in window) {
      try { caches.keys().then((names) => names.forEach((n) => caches.delete(n))); } catch {}
    }
    if ('serviceWorker' in navigator) {
      try { navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())); } catch {}
    }
    const url = new URL(window.location.href);
    url.searchParams.set('cb', String(now));
    setTimeout(() => {
      try { window.location.replace(url.toString()); }
      catch { try { window.location.reload(); } catch {} }
    }, 50);
    return true;
  } catch {
    return false;
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null, variant: 'crash', busy: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorId: `err_${Date.now()}`, variant: pickErrorVariant(error) };
  }

  componentDidCatch(error, info) {
    console.error('[App] Erro não tratado:', error, info);
    const message = String(error?.message || error);
    // Auditoria 18/05 + 25/08: lazy chunk/CSS 303 e Stream "channel after
    // disconnect" — recupera com reload (cooldown 60s) ANTES de inflar
    // error_boundary. telemetry/errors.js cobre window.error; o React
    // captura lazy import no ErrorBoundary sem passar pelos handlers globais.
    if (tryErrorBoundaryRecover(message)) return;
    logUxEvent('error_boundary', {
      error_message: message,
      error_name: error?.name || 'Error',
      error_stack: error?.stack || null,
    });
    try {
      localStorage.setItem(
        'last_app_error',
        JSON.stringify({
          id: this.state.errorId,
          message,
          at: new Date().toISOString(),
        })
      );
    } catch {}
  }

  handlePrimary = () => {
    if (this.state.busy) return;
    this.setState({ busy: true });
    try {
      logUxEvent('error_screen_action', {
        variant: this.state.variant,
        action: 'primary',
        route: window.location.pathname,
        error_id: this.state.errorId,
      });
    } catch {}
    if (this.state.variant === 'update') reloadFreshVersion();
    else retryWithEscalation();
  };

  render() {
    if (this.state.hasError) {
      const showHome = getRetryCount() >= RETRY_SHOW_HOME_AFTER;
      return (
        <ErrorScreen
          variant={this.state.variant}
          onPrimary={this.handlePrimary}
          busy={this.state.busy}
          onHome={showHome ? goHome : undefined}
          errorId={this.state.errorId}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Componente de redirect para /baixar
 */
function BaixarRedirect() {
  useEffect(() => {
    window.location.replace('https://e6zdv.app.link/9Cgdtk1rhVb');
  }, []);
  return null;
}

/**
 * /avalie — detecta SO e redireciona direto para a página de avaliação.
 * iOS  → App Store (abre tela de escrever review)
 * Android → Play Store (app intercepta e abre na loja)
 * Desktop → /reviews
 */
function IndiqueWhatsappRedirect() {
  useEffect(() => {
    window.location.replace(buildIndiqueWaUrl());
  }, []);
  return null;
}

function AvalieRedirect() {
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);

    if (isIOS) {
      window.location.replace(
        'https://apps.apple.com/br/app/consulta-mais/id6748291915?action=write-review'
      );
    } else if (isAndroid) {
      window.location.replace(
        'https://play.google.com/store/apps/details?id=com.consultamais.app4&hl=pt_BR'
      );
    } else {
      window.location.replace('/reviews');
    }
  }, []);
  return null;
}

const ALLOWED_WHILE_ACTIVE = [
  '/consulta/chat',
  '/consulta/chat/pago',
  '/promo',
  '/promo2',
  // Funil de cadastro: paciente com consulta aberta que usa Voltar no browser
  // nao deve entrar em loop replaceState (/email <-> /consulta/chat). Auditoria 02/06.
  '/consulta',
  '/email',
  '/email/confirm',
  '/cpf',
  '/cupom-primeira',
  '/telefone',
  '/termos',
  // A/B de ordem — braço `doc_caf` (foto de RG/CNH/CIN).
  '/cadastro/documento',
  '/pagamento',
  '/triagem',
];

/** Preserva query/hash ao redirecionar rotas legadas ou aliases de e-mail. */
function RedirectKeepQuery({ to }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
}

/**
 * Layout "guardião" para todas as rotas do paciente.
 * - Se houver consulta aberta (activeChannelId) e não finalizada (sem finalizedAt),
 *   redireciona para /consulta/chat ou /consulta/chat/pago (dependendo do pagamento),
 *   EXCETO para rotas explicitamente permitidas.
 * - Também lida com registro do token FCM no AndroidBridge.
 */
const PatientLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeChan = localStorage.getItem('activeChannelId');
  const finalized  = localStorage.getItem('finalizedAt');
  const layoutPatientId = localStorage.getItem('patientId') || '';

  useEffect(() => {
    // Canal só conta se pertencer ao patientId atual (caso Arthur Dias
    // 03/08/2026: activeChannelId órfão de consulta anterior furava o
    // funil e mandava o paciente pro chat sem triagem desta sessão).
    const channelOwned =
      !!activeChan &&
      !!layoutPatientId &&
      isActiveChannelOwnedBy(layoutPatientId, activeChan);

    if (activeChan && !channelOwned) {
      try {
        localStorage.removeItem('activeChannelId');
        sessionStorage.removeItem('activeChannelId');
      } catch {}
      return;
    }

    // Só redireciona se NÃO está em rota permitida
    if (
      channelOwned &&
      !finalized &&
      !ALLOWED_WHILE_ACTIVE.includes(location.pathname)
    ) {
      // Decide a rota baseado no pagamento — só conta como "pago" se o
      // flag estiver ligado ao patientId atual. Evita que flag vazado
      // de consulta anterior mande paciente direto para /pago.
      const isPaid = layoutPatientId ? isPaymentConfirmedFor(layoutPatientId) : false;
      const targetRoute = isPaid ? '/consulta/chat/pago' : '/consulta/chat';
      navigate(targetRoute, { replace: true });
    }
  }, [activeChan, finalized, layoutPatientId, location.pathname, navigate]);

  /* Estorno detectado com a sessão aberta.
   *
   * A reconciliação do boot cobre quem reabre o app, mas o estorno costuma ser
   * processado (Sofia, worker agendado, dashboard, painel do gateway) enquanto
   * o paciente está com o chat aberto esperando. Sem esta revalidação ele
   * continuaria olhando uma tela "paga" sem como pagar de novo.
   *
   * A janela é curta de propósito (PAYMENT_RECHECK_MS): o paciente estornado
   * precisa reencontrar o caminho do pagamento em segundos, não no próximo boot.
   * Custo baixo porque só roda enquanto o flag local diz "pago" (pagamento
   * ativo sai do Firestore sem tocar o gateway) e para de vez quando limpa.
   * Toque na tela e volta de foco/rede disparam na hora.
   *
   * O reload é o que faz o ConsultaChat reler o flag; um guard por patientId na
   * sessão evita qualquer chance de laço. */
  useEffect(() => {
    if (!layoutPatientId) return;
    if (localStorage.getItem('loginRole') === 'doctor') return;
    if (new URLSearchParams(location.search).get('as') === 'doctor') return;
    if (!isPaymentConfirmedFor(layoutPatientId)) return;

    let stopped = false;
    let lastRunAt = 0;

    const reloadKey = `paymentRefundReloadedFor:${layoutPatientId}`;

    const recheck = async () => {
      if (stopped) return;
      if (!isPaymentConfirmedFor(layoutPatientId)) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (Date.now() - lastRunAt < PAYMENT_RECHECK_MIN_GAP_MS) return;
      lastRunAt = Date.now();

      const result = await migrateLegacyFlag(layoutPatientId, { baseUrl: BACKEND_URL });
      if (stopped || !result?.cleared) return;

      try {
        logUxEvent('payment_flag_cleared', {
          source: result.source,
          patientId: layoutPatientId,
          surface: 'patient_layout_recheck',
        });
      } catch {}

      let alreadyReloaded = false;
      try { alreadyReloaded = sessionStorage.getItem(reloadKey) === '1'; } catch {}
      if (alreadyReloaded) return;
      try { sessionStorage.setItem(reloadKey, '1'); } catch {}
      window.location.reload();
    };

    const onWake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      recheck();
    };

    recheck();
    const intervalId = setInterval(recheck, PAYMENT_RECHECK_MS);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    window.addEventListener('pointerdown', onWake, { passive: true });

    return () => {
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      window.removeEventListener('pointerdown', onWake);
    };
  }, [layoutPatientId, location.pathname, location.search]);

  // Registro do token FCM
  useEffect(() => {
    const patientId = localStorage.getItem('patientId') || '';
    if (!patientId) return;

    if (window.AndroidBridge?.savePatientId) {
      window.AndroidBridge.savePatientId(patientId);
    }
    if (window.AndroidBridge?.requestFcmToken) {
      window.AndroidBridge.requestFcmToken();
    }
    window.onFcmTokenReceived = async (token) => {
      if (!token) return;
      try {
        await fetch(`${BACKEND_URL}/register_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: patientId,
            fcmToken: token,
          }),
        });
      } catch (err) {
        console.error('Falha ao registrar FCM token:', err);
      }
    };
    return () => {
      delete window.onFcmTokenReceived;
    };
  }, []);

  // Home e longa: rodape fixo no viewport sobrepoe FAQ/carrossel ao rolar (PC e mobile).
  // O contato de suporte fica no fim do documento via PatientSupportFooter static em Home.jsx.
  // /reviews e /diretor sao institucionais (LegalPageShell com rodape proprio) — sem o fixo.
  const NO_FIXED_SUPPORT_FOOTER = new Set(['/', '/reviews', '/diretor']);
  const useFixedSupportFooter = !NO_FIXED_SUPPORT_FOOTER.has(location.pathname);
  // O CNPJ é chave Pix da conta MP: copiado do rodapé, o Pix entra sem vínculo com a consulta.
  const HIDE_CNPJ_FOOTER = new Set(['/pagamento', '/consulta/chat', '/consulta/chat/pago']);
  const hideFooterCnpj = HIDE_CNPJ_FOOTER.has(location.pathname.replace(/\/+$/, ''));

  return (
    <>
      <AnimatedOutlet />
      {useFixedSupportFooter && <PatientSupportFooter hideCnpj={hideFooterCnpj} />}
    </>
  );
};

const ScreenViewTracker = () => {
  useScreenView();
  usePrefetchNextRoute();
  return null;
};

/**
 * No .org, /promo é a URL final dos RSAs Search-6. Tem de ficar no mesmo
 * domínio e mostrar a Home — Promo.jsx é o programa de indicação e o
 * pulo para consultamais.app dispara "Destino não correspondente".
 */
function OrgLandingOrPromo() {
  return isOrgHost() ? <Home /> : <Promo />;
}

function OrgLandingOrPromo2() {
  return isOrgHost() ? <Home /> : <PromoScreen />;
}

/**
 * Domínio espelho consultamais.org (landing dos anúncios Google Ads).
 * Serve só a Home/institucional no .org; o funil (consulta, termos,
 * pagamento, triagem, chat) redireciona para o consultamais.app
 * preservando gclid/UTMs. O apiV2 não libera CORS do .org.
 * Ver src/lib/siteHosts.js e docs/ads/org-firebase-setup.md.
 */
const OrgHostGuard = () => {
  const location = useLocation();

  useEffect(() => {
    if (!isOrgHost()) return;
    captureLandingParams();
    if (!isOrgAllowedPath(location.pathname)) {
      window.location.replace(buildAppUrl(location.pathname + location.search));
    }
  }, [location]);

  return null;
};

/**
 * Componente que verifica periodicamente se há nova versão disponível.
 * - Se forceUpdate: mostra modal bloqueante (obrigatório atualizar)
 * - Senão: mostra banner opcional no rodapé
 */
const ChatWidgetGuard = () => {
  const location = useLocation();
  const p = location.pathname;
  // Rotas administrativas (médico/suporte/notafiscal/programa Amigo/etc.) não
  // têm o widget — usuários internos não precisam dele.
  const isAdmin = /^\/(suporte|medico|medicopainel|notafiscal|amigo|meuprogramaamigo|divulgue|indique|controle|cupons|ux-monitor)(\/|$)/i.test(p);
  // Rotas /embed/** renderizam o próprio widget em tela cheia, então não
  // podemos montar uma segunda instância aqui (senão o dialog aparece duas
  // vezes dentro do iframe).
  const isEmbed = /^\/embed(\/|$)/i.test(p);
  // Paciente: Sofia visível em todas as telas (cadastro, triagem, chat, pagamento).
  // O widget some com o teclado virtual aberto e pode ser dispensado com o X no FAB.
  return isAdmin || isEmbed ? null : <SupportChatWidget />;
};

const VersionChecker = () => {
  const { updateAvailable, forceUpdate, newVersion, currentVersion, triggerUpdate, updateMessage } = useVersionCheck();

  if (forceUpdate) {
    return (
      <ForceUpdateModal
        onUpdate={triggerUpdate}
        newVersion={newVersion}
        currentVersion={currentVersion}
        message={updateMessage}
      />
    );
  }

  if (!updateAvailable) return null;
  return <UpdateBanner onUpdate={triggerUpdate} newVersion={newVersion} />;
};

export default function App() {
  useEffect(() => {
    hydrateAttributionFromBackups();
    installPaymentAttributionInterceptor();
    hardRefreshIfStale();
    initTelemetry();
    setUxUserFromStorage();

    // Prefetch idle dos chunks da jornada do paciente. Auditoria UX 28/06
    // mostrou LCP P75 9s em /consulta/chat porque o chunk lazy do ConsultaChat
    // (com stream-chat-react) so chegava no momento da navegacao em iOS lento.
    // webpackPrefetch no import() nao era respeitado pelo react-scripts 5;
    // disparamos manualmente via requestIdleCallback para carregar em paralelo
    // depois da home estabilizar. NAO bloqueia render — apenas baixa em cache.
    // Pulado em rotas administrativas para nao gastar banda do medico/admin.
    try {
      const path = window.location.pathname || '';
      const isAdminPath = /^\/(medico|admin|controle|suporte|admin-health|painel-condutas|painel-receitas|amigo|conteudo|noticias|social|reviews|diretor|transparencia-ia|privacidade|lgpd|dados-exclusao|termos|terms|baixar|avalie|indique|divulgue|autenticar|data-atestado|consulta-medica-por-chat|plataformas-teleconsulta-chat|consulta-mais-e-consulta-plus|ec-cmchatmedico|mp-cmchatmedico|cmchatmedico)\b/i.test(path);
      // Em conexoes lentas/save-data, nao prefeta — paciente prefere economia de dados.
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const slowNet = conn && (conn.saveData || /^(slow-2g|2g)$/i.test(String(conn.effectiveType || '')));
      if (isAdminPath || slowNet) return undefined;
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
      const handle = schedule(() => {
        // Ordenado pela jornada: Pagamento -> AnamneseChat (triagem) -> ConsultaChat.
        // import() carrega + cacheia; quando o usuario chega na rota, mount eh instantaneo.
        import('./Pagamento').catch(() => {});
        import('./AnamneseChat').catch(() => {});
        import('./ConsultaChat').catch(() => {});
      });
      return () => {
        try {
          if (typeof handle === 'number' && window.cancelIdleCallback) {
            window.cancelIdleCallback(handle);
          }
        } catch {}
      };
    } catch {
      return undefined;
    }
  }, []);
  return (
    <ErrorBoundary>
      <Router>
        <EmailPreviewGate>
        <OrgHostGuard />
        <ScrollToTop />
        <SignupHistoryGuard />
        <ScreenViewTracker />
        <VersionChecker />
        <ChatWidgetGuard />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Redirects utilitários */}
            <Route path="baixar" element={<BaixarRedirect />} />
            <Route path="avalie" element={<AvalieRedirect />} />

            {/* Indicação: só abre o WhatsApp com mensagem pronta */}
            <Route path="indique" element={<IndiqueWhatsappRedirect />} />

            {/* Alias de e-mail promocional — mantém utm_* ao cair na Home */}
            <Route path="home" element={<RedirectKeepQuery to="/" />} />

            {/* Rotas legadas da landing estática anterior */}
            <Route path="amigo/inicio" element={<RedirectKeepQuery to="/amigo" />} />
            <Route path="consulta/chat/inicio" element={<RedirectKeepQuery to="/consulta/chat" />} />
            <Route path="autenticar/inicio" element={<RedirectKeepQuery to="/autenticar" />} />

            {/* Todas as rotas do paciente passam pelo PatientLayout */}
            <Route element={<PatientLayout />}>
              <Route index                     element={<Home />} />
              <Route path="consulta"           element={<NameForm />} />
              <Route path="email/confirm" element={<EmailConfirmForm />} />
              <Route path="email" element={<EmailForm />} />
              <Route path="cpf"   element={<CpfForm />} />
              <Route path="cupom-primeira" element={<InstantCouponOffer />} />
              <Route path="telefone" element={<PhoneForm />} />
              <Route path="cadastro/documento" element={<DocumentIdForm />} />
              <Route path="termos"             element={<TermsOfUse />} />
              <Route path="pagamento"          element={<PaymentScreen />} />
              <Route path="triagem"            element={<AnamneseChat />} />
              <Route path="consulta/chat/pago"   element={<ConsultaChat />} />
              <Route path="consulta/chat"        element={<ConsultaChat />} />
              <Route path="consulta/recuperar" element={<Navigate to="/" replace />} />
              <Route path="promo2"             element={<OrgLandingOrPromo2 />} />
              <Route path="promo"              element={<OrgLandingOrPromo />} />
              <Route path="lembrete"           element={<PrescriptionReminderPage />} />
              <Route path="acompanhamento"     element={<CareReminderPage />} />

              {/* Avaliações e Diretor Técnico ficam sob o PatientLayout para
                  herdar o fade-out/fade-in da Home (AnimatedOutlet). */}
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="diretor" element={<DiretorPage />} />
            </Route>

            {/* Fluxo do médico — /medico/painel antes do splat /medico/* */}
            <Route path="medico/painel" element={withAdminGuard(['doctor', 'admin'], <MedicoPainel />, 'Painel de rascunhos')} />
            <Route path="medico/*" element={withAdminGuard(['doctor', 'admin'], <DoctorDashboard />, 'Painel médico')} />
            <Route path="medicopainel" element={withAdminGuard(['doctor', 'admin'], <MedicoPainel />, 'Painel de rascunhos')} />

            {/* Painel de suporte (admin) */}
            <Route path="suporte" element={withAdminGuard(['support', 'admin'], <SupportDashboard />, 'Painel de suporte')} />

            {/* Painel de controle financeiro (admin) */}
            <Route path="controle" element={withAdminGuard(['admin', 'finance'], <ControleDashboard />, 'Controle financeiro')} />

            {/* Painel de cupons e indicações (admin) */}
            <Route path="cupons" element={withAdminGuard(['admin', 'finance'], <CouponsDashboard />, 'Cupons & indicações')} />

            {/* Monitor de UX (admin) */}
            <Route path="ux-monitor" element={withAdminGuard(['admin'], <UXMonitorDashboard />, 'UX monitor')} />

            {/* Emissão de NFS-e (admin) */}
            <Route path="notafiscal" element={withAdminGuard(['admin', 'finance'], <NotaFiscal />, 'NFS-e')} />
            <Route path="notafiscalantiga" element={withAdminGuard(['admin', 'finance'], <NotaFiscalAntiga />, 'NFS-e (legado)')} />

            {/* Política de Privacidade */}
            <Route path="lgpd" element={<PrivacyPolicy />} />
            <Route path="dados-exclusao" element={<DadosExclusao />} />
            <Route path="segunda-via" element={<SegundaVia />} />

            {/* Termos de Uso (visualização pública avulsa, sem aceite —
                usada nos comunicados em massa quando há atualização) */}
            <Route path="termos-de-uso" element={<TermsOfUsePublic />} />

            {/* Transparência sobre uso de IA — Resolução CFM 2.454/2026
                (Anexo III-I + Art. 11). Linkada pelo AiAssistedBadge no
                chat do paciente e referenciada nos Termos (cláusula 9). */}
            <Route path="transparencia-ia" element={<TransparenciaIA />} />

            {/* /reclameaqui retirada do ar em 13/08/2026 (Navigate + 301). */}
            <Route path="reclameaqui" element={<ReclameAquiPage />} />

            {/* /avalieapp retirada do ar em 15/08/2026 (Navigate + 301). */}
            <Route path="avalieapp" element={<AvalieAppPage />} />

            {/* Sofia embutida em iframe da landing promo.consultamais.org.
                Sem chrome do PatientLayout — só o widget em tela cheia.
                Headers /embed/** no firebase.json liberam frame-ancestors. */}
            <Route path="embed/sofia" element={<EmbedSofia />} />

            {/* Painel admin de saúde do backend (OpenAI / Resend / triagens).
                Protegido por ADMIN_TOKEN no backend. Onda 1B: tambem por
                AdminGuard quando REACT_APP_ADMIN_REQUIRE_LOGIN=true. */}
            <Route path="admin/health" element={withAdminGuard(['admin'], <AdminHealthDashboard />, 'Saúde da API')} />

            {/* Autenticação de atestado digital */}
            <Route path="autenticar" element={<Autenticar />} />

            {/* Prévia do documento antes do pagamento — destino do link que vai
                no e-mail, já que e-mail não roda o timer nem o blur. */}
            <Route path="previa/:token" element={<PreviaDocumento />} />

            {/* FAQ pública sobre a data em documentos emitidos por
                teleconsulta assíncrona (03/08/2026). Referenciada pelos
                canais de reembolso (chat modal, Sofia, e-mails), como
                antídoto à ansiedade "vou perder o dia do atestado" que
                empurra parte dos self-refunds. */}
            <Route path="data-atestado" element={<DataAtestado />} />

            {/* Easycheck — verificação fácil / relatório formal pago (B2B, RH). */}
            <Route path="easycheck" element={<Fastcheck />} />
            <Route path="easycheck/termos" element={<FastcheckTerms />} />
            <Route path="fastcheck" element={<Navigate to="/easycheck" replace />} />
            <Route path="fastcheck/termos" element={<Navigate to="/easycheck/termos" replace />} />

            {/* Programa de indicação (público). Voltou ao ar em 14/08/2026 SEM
                a Copa dos Amigos do Peito: o abatimento ao paciente cabe na
                permissão expressa do art. 9º, VIII da Res. CFM 2.336/2023, mas
                ranking com prêmio anunciado é a "premiação" que o mesmo inciso
                veda. As páginas da Copa seguem fora do ar e caem na Home; só o
                regulamento continua acessível, porque rege a apuração de 2026 e
                a premiação de jan/2027, que estão mantidas. Critério em
                docs/compliance/publicidade-programa-indicacao.md. O splat fica
                por último de propósito: no React Router v6 segmento estático tem
                precedência sobre `*`, então /amigo/copa/termos continua
                resolvendo. */}
            <Route path="amigo" element={<AmigoIntro />} />
            <Route path="amigo/nome" element={<AmigoStepNome />} />
            <Route path="amigo/cpf" element={<AmigoStepCpf />} />
            <Route path="amigo/email" element={<AmigoStepEmail />} />
            <Route path="amigo/email-confirmar" element={<AmigoStepEmailConfirm />} />
            <Route path="amigo/telefone" element={<AmigoStepTelefone />} />
            <Route path="amigo/aceite" element={<AmigoStepAceite />} />
            <Route path="amigo/pronto" element={<AmigoStepPronto />} />
            <Route path="amigo/termos" element={<AmigoProgramTerms />} />
            <Route path="amigo/copa/termos" element={<CopaAmigosDoPeitoTerms />} />
            {/* Links antigos da Copa (e-mails de jul-ago/2026) caem no
                regulamento, não na Home nem no cadastro: é lá que está a nota
                explicando que a divulgação acabou e que a apuração de 2026 e a
                premiação de jan/2027 seguem valendo. */}
            <Route path="amigo/copa" element={<RedirectKeepQuery to="/amigo/copa/termos" />} />
            <Route path="amigo/copa/ranking" element={<RedirectKeepQuery to="/amigo/copa/termos" />} />
            <Route path="amigo/copa/ranking/completo" element={<RedirectKeepQuery to="/amigo/copa/termos" />} />
            <Route path="amigo/desafio" element={<RedirectKeepQuery to="/amigo/copa/termos" />} />
            <Route path="amigo/desafio/termos" element={<RedirectKeepQuery to="/amigo/copa/termos" />} />
            <Route path="melhores-amigos/termos" element={<MelhoresAmigosProgramTerms />} />
            <Route path="meuprogramaamigo" element={<MeuProgramaAmigo />} />
            <Route path="amigo/*" element={<RedirectKeepQuery to="/amigo" />} />
            <Route path="promo/primeira-consulta/termos" element={<FirstConsultPromoTerms />} />
            <Route path="concurso-video/termos" element={<VideoConcursoTerms />} />
            <Route path="divulgue" element={<DivulguePage />} />

            {/* Conteudo publico (SEO/GEO): portal /noticias + artigos em
                /conteudo/:slug. Pre-renderizado estaticamente no build
                (scripts/prerender-conteudo.js). O hub antigo /conteudo
                redireciona para /noticias (301 tambem no firebase.json). */}
            <Route path="noticias" element={<NoticiasPortal />} />
            <Route path="noticias/categoria/:categoryId" element={<NoticiasCategoria />} />
            <Route path="conteudo" element={<Navigate to="/noticias" replace />} />
            <Route path="conteudo/:slug" element={<ConteudoArticle />} />
            <Route path="imprensa" element={<Navigate to="/conteudo/imprensa" replace />} />

            {/* Hubs de descoberta GEO (ago/2026) — paginas-entidade otimizadas
                para queries de descoberta em LLMs (Gemini/ChatGPT/Perplexity).
                Nao lazy: sao paginas pequenas, institucionais, e o first paint
                estatico ajuda crawlers sem JS. */}
            <Route
              path="consulta-medica-por-chat"
              element={<HubConsultaMedicaPorChatPage />}
            />
            <Route
              path="plataformas-teleconsulta-chat"
              element={<HubPlataformasTeleconsultaPage />}
            />
            <Route
              path="consulta-mais-e-consulta-plus"
              element={<HubConsultaMaisConsultaPlusPage />}
            />
            <Route path="ec-cmchatmedico" element={<HubCobrancaCartaoPage />} />
            <Route path="mp-cmchatmedico" element={<Navigate to="/ec-cmchatmedico" replace />} />
            <Route path="cmchatmedico" element={<Navigate to="/ec-cmchatmedico" replace />} />

            {/* Divulga Saúde — canônico /marli;
                /social redireciona para não quebrar links antigos. */}
            <Route path="marli" element={<SocialPage />} />
            <Route path="social" element={<Navigate to="/marli" replace />} />

            {/* Benefício de consulta (R$ 9,90) — canônico /cupom e /cupom/termos;
                rotas /marli/* e /social/* redirecionam. */}
            <Route path="cupom" element={<SocialMarlieteCupomPage />} />
            <Route path="cupom/termos" element={<MarlieteCouponTerms />} />
            <Route path="marli/cupom" element={<Navigate to="/cupom" replace />} />
            <Route path="marli/cupom-gratuito" element={<Navigate to="/cupom" replace />} />
            <Route path="marli/termos" element={<Navigate to="/cupom/termos" replace />} />
            <Route path="social/cupom" element={<Navigate to="/cupom" replace />} />
            <Route path="social/cupom-gratuito" element={<Navigate to="/cupom" replace />} />
            <Route path="social/termos" element={<Navigate to="/cupom/termos" replace />} />

            {/* Qualquer rota desconhecida redireciona para home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </EmailPreviewGate>
      </Router>
    </ErrorBoundary>
  );
}